import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AffiliateDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { if (ready && !user) navigate("/login"); }, [ready, user]); // eslint-disable-line
  useEffect(() => { if (user) api.get("/affiliates/me").then(({ data }) => setData(data)).catch(() => setData(false)); }, [user]);

  if (!data) return <div className="py-32 text-center font-heading text-3xl text-ink/40">Loading…</div>;
  if (data === false) return <div className="py-32 text-center font-heading text-2xl">No affiliate profile found.</div>;

  const a = data.affiliate;
  const link = `${window.location.origin}/shop?ref=${a.code}`;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied to clipboard."); };

  const stats = [
    ["Status", a.status, true], ["Commission", `${a.commission_rate}%`], ["Clicks", a.clicks],
    ["Conversions", a.conversions], ["Unpaid Earnings", `$${(a.unpaid_earnings || 0).toFixed(2)}`], ["Total Earned", `$${(a.total_earnings || 0).toFixed(2)}`],
  ];

  return (
    <div className="px-6 md:px-12 py-14 mx-auto max-w-[1000px]" data-testid="affiliate-dashboard">
      <div className="flex justify-between items-end border-b hairline pb-6 mb-10">
        <div><p className="eyebrow mb-2">Ambassador Dashboard</p><h1 className="font-heading text-4xl tracking-tight">Welcome, {a.name?.split(" ")[0]}</h1></div>
        <button onClick={() => { logout(); navigate("/"); }} className="font-body text-[0.7rem] uppercase tracking-wide2 text-ink/60 hover:text-ink">Sign Out</button>
      </div>

      {a.status !== "approved" && <div className="bg-warm/40 p-4 mb-8 font-body text-sm">Your application is <b>{a.status}</b>. You'll be notified once approved by our team.</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
        {stats.map(([l, v], i) => (
          <div key={i} className="border hairline p-5">
            <p className="eyebrow mb-2">{l}</p>
            <p className="font-heading text-3xl capitalize">{v}</p>
          </div>
        ))}
      </div>

      <div className="space-y-4 mb-10">
        <div className="border hairline p-5">
          <p className="eyebrow mb-2">Your Referral Link</p>
          <div className="flex items-center justify-between gap-3"><code className="font-body text-sm break-all">{link}</code><button onClick={() => copy(link)} className="icon-thin shrink-0" data-testid="copy-link"><Copy size={16} /></button></div>
        </div>
        <div className="border hairline p-5">
          <p className="eyebrow mb-2">Your Promo Code</p>
          <div className="flex items-center justify-between"><span className="font-heading text-3xl tracking-wide">{a.code}</span><button onClick={() => copy(a.code)} className="icon-thin"><Copy size={16} /></button></div>
        </div>
      </div>

      <h2 className="font-heading text-2xl mb-4">Recent Conversions</h2>
      {data.conversions.length === 0 ? <p className="font-body text-sm text-ink/50">No conversions yet — share your link to get started.</p> : (
        <div className="border hairline divide-y hairline">
          {data.conversions.map((c) => (
            <div key={c.id} className="flex justify-between p-4 font-body text-sm">
              <span>{c.order_number} · {new Date(c.created_at).toLocaleDateString()}</span>
              <span className="font-medium">+${c.commission.toFixed(2)} {c.paid && <em className="text-ink/50 not-italic">(paid)</em>}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
